import React, { useContext, useState, useMemo, useEffect, useRef } from "react";
import { RemixBuilderContext } from "../remix/shared/RemixBuilderContext";
import RemixBuilderSettingsTheme from "../remix/shared/RemixBuilderSettingsTheme";
import RemixBuilderSettingsBreadcrumb from "../remix/shared/RemixBuilderSettingsBreadcrumb";
import RemixModalLoader from "../remix/shared/RemixModalLoader";
import TemplatePreviewModalSettingsDetails from "./TemplatePreviewModalSettingsDetails";

const STEP_OPTIONS = [
  { step: 1, name: "Theme" },
  { step: 2, name: "Details" },
];

function extractStyleValue(val) {
  if (val == null) return null;
  if (typeof val === "object" && "value" in val) return val.value;
  if (typeof val === "object" && "hex" in val) return val.hex;
  return typeof val === "string" ? val : String(val);
}

function buildCtaStyleParams(remixStyles, brandStyle, remixMappings) {
  // Only apply theme styling when user has selected a theme (remixStyles).
  // Do not default to brand styles for the preview.
  const params = {};

  if (!remixStyles) return params;

  // Fonts
  if (remixStyles["heading-font-family"]) {
    const val = extractStyleValue(remixStyles["heading-font-family"]);
    if (val) params.brand_font_headline = val;
  }
  if (remixStyles["paragraph-font-family"]) {
    const val = extractStyleValue(remixStyles["paragraph-font-family"]);
    if (val) params.brand_font_paragraph = val;
  }

  // Granular remix color params (1:1 with theme inputs)
  if (remixStyles["heading-font-color"]) {
    const val = extractStyleValue(remixStyles["heading-font-color"]);
    if (val) params.heading_font_color = val;
  }
  if (remixStyles["paragraph-font-color"]) {
    const val = extractStyleValue(remixStyles["paragraph-font-color"]);
    if (val) params.paragraph_font_color = val;
  }
  if (remixStyles["button-color"]) {
    const val = extractStyleValue(remixStyles["button-color"]);
    if (val) params.button_color = val;
  }
  if (remixStyles["button-text-color"]) {
    const val = extractStyleValue(remixStyles["button-text-color"]);
    if (val) params.button_text_color = val;
  }
  if (remixStyles["accent-color"]) {
    const val = extractStyleValue(remixStyles["accent-color"]);
    if (val) params.accent_color = val;
  }
  if (remixStyles["background-color"]) {
    const val = extractStyleValue(remixStyles["background-color"]);
    if (val) params.background_color = val;
  }

  delete params.brand_color_dark;
  delete params.brand_color_light;
  delete params.brand_color_vibrant;

  return params;
}

function buildSectionStyleParams(remixStyles, remixMappings) {
  if (!remixStyles || !remixMappings) return {};

  const parsed_options = {};

  if (
    remixStyles["heading-font-family"] &&
    remixMappings["heading-font-family"]
  ) {
    remixMappings["heading-font-family"].forEach((option) => {
      parsed_options[option] =
        remixStyles["heading-font-family"]?.value ||
        remixStyles["heading-font-family"];
    });
  }
  if (
    remixStyles["heading-font-color"] &&
    remixMappings["heading-font-color"]
  ) {
    const val = extractStyleValue(remixStyles["heading-font-color"]);
    if (val)
      remixMappings["heading-font-color"].forEach((option) => {
        parsed_options[option] = val;
      });
  }
  if (
    remixStyles["paragraph-font-family"] &&
    remixMappings["paragraph-font-family"]
  ) {
    remixMappings["paragraph-font-family"].forEach((option) => {
      parsed_options[option] =
        remixStyles["paragraph-font-family"]?.value ||
        remixStyles["paragraph-font-family"];
    });
  }
  if (
    remixStyles["paragraph-font-color"] &&
    remixMappings["paragraph-font-color"]
  ) {
    const val = extractStyleValue(remixStyles["paragraph-font-color"]);
    if (val)
      remixMappings["paragraph-font-color"].forEach((option) => {
        parsed_options[option] = val;
      });
  }
  if (remixStyles["button-color"] && remixMappings["button-color"]) {
    const val = extractStyleValue(remixStyles["button-color"]);
    if (val)
      remixMappings["button-color"].forEach((option) => {
        parsed_options[option] = val;
      });
  }
  const buttonTextColorSource =
    remixStyles["button-text-color"] ??
    (remixStyles["button-color"] ? "#ffffff" : null);
  if (buttonTextColorSource && remixMappings["button-text-color"]) {
    const val = extractStyleValue(buttonTextColorSource);
    if (val)
      remixMappings["button-text-color"].forEach((option) => {
        parsed_options[option] = val;
      });
  }
  if (remixStyles["accent-color"] && remixMappings["accent-color"]) {
    const val = extractStyleValue(remixStyles["accent-color"]);
    if (val)
      remixMappings["accent-color"].forEach((option) => {
        parsed_options[option] = val;
      });
  }
  if (remixStyles["logo-style"] && remixMappings["logo-style"]) {
    const val = extractStyleValue(remixStyles["logo-style"]);
    if (val) parsed_options["logo-style"] = val;
  }
  if (remixStyles["logo-image"] && remixMappings["logo-image"]) {
    const val = extractStyleValue(remixStyles["logo-image"]);
    if (val) parsed_options["logo-image"] = val;
  }

  return parsed_options;
}

function buildIframeSrc(
  templateData,
  deviceType,
  brandStyle,
  rootUrl,
  remixSettings,
  remixMappings
) {
  if (!templateData?.template) return "";

  const { template, object } = templateData;

  if (template.object_type === "Cta") {
    const styleParams = buildCtaStyleParams(
      remixSettings?.styles,
      brandStyle || {},
      remixMappings
    );

    let src =
      rootUrl +
      "/websites/0/cta/" +
      object.id +
      "/page/?iframe=true&variant=&device_type=" +
      deviceType;

    Object.keys(styleParams).forEach((key) => {
      if (styleParams[key]) {
        src += "&" + key + "=" + encodeURIComponent(styleParams[key]);
      }
    });
    return src;
  }

  if (["Section", "Step", "Element", "Field"].includes(template.object_type)) {
    const targetController = {
      Section: "sections",
      Step: "steps",
      Element: "elements",
      Field: "fields",
    }[template.object_type];

    let src =
      rootUrl + `/websites/0/${targetController}/${template.object_id}/preview`;

    const parsedOptions = buildSectionStyleParams(
      remixSettings?.styles,
      remixMappings
    );
    // Always include background fallback to white so preview has a background before theme is selected
    const optionsWithFallback = {
      "background-color": "#ffffff",
      "container-background": "light",
      ...parsedOptions,
    };
    src +=
      "?" +
      new URLSearchParams({
        variant: JSON.stringify({ options: optionsWithFallback }),
      }).toString();
    return src;
  }

  return "";
}

const TemplatePreviewModal = (props) => {
  const {
    templateData,
    onClose,
    createEndpoint,
    redirectPath,
    brandStyle,
  } = props;

  const modalRef = useRef(null);
  const iframeContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const iframeWrapperRef = useRef(null);
  const [contextData] = useContext(RemixBuilderContext);
  const [deviceType, setDeviceType] = useState("desktop");
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [fitScale, setFitScale] = useState(0.9);
  const [mobileScale, setMobileScale] = useState(0.9);

  useEffect(() => {
    if (!iframeContainerRef.current) return;
    const updateDimensions = () => {
      if (!iframeContainerRef.current) return;
      const { clientWidth, clientHeight } = iframeContainerRef.current;
      if (clientWidth <= 0 || clientHeight <= 0) return;
      setContainerDimensions((prev) =>
        prev.width === clientWidth && prev.height === clientHeight
          ? prev
          : { width: clientWidth, height: clientHeight }
      );
      const isCta = templateData?.template?.object_type === "Cta";
      if (isCta && deviceType === "desktop") {
        const scaleX = clientWidth / 1332;
        const fitScaleValue = Math.max(0.5, Math.min(scaleX * 0.98, 1.5));
        setFitScale((prev) => (prev === fitScaleValue ? prev : fitScaleValue));
      }
      if (deviceType === "mobile") {
        const scaleX = clientWidth / 375;
        const scaleY = clientHeight / 667;
        const mobileScaleValue = Math.max(0.5, Math.min(Math.min(scaleX, scaleY) * 0.98, 1));
        setMobileScale((prev) => (prev === mobileScaleValue ? prev : mobileScaleValue));
      }
    };
    updateDimensions();
    let frameId = null;
    const handleResize = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateDimensions();
      });
    };
    const observer = new ResizeObserver(handleResize);
    observer.observe(iframeContainerRef.current);
    const timeoutId = setTimeout(updateDimensions, 100);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [templateData, deviceType]);

  const rootUrl = window.location.origin;

  const iframeSrc = useMemo(
    () =>
      buildIframeSrc(
        templateData,
        deviceType,
        brandStyle,
        rootUrl,
        contextData?.remix_settings,
        contextData?.remix_mappings
      ),
    [
      templateData,
      deviceType,
      brandStyle,
      rootUrl,
      contextData?.remix_settings,
      contextData?.remix_mappings,
    ]
  );

  useEffect(() => {
    const container = iframeContainerRef.current;
    const iframe = iframeRef.current;
    if (!container || !iframe) return;

    const isCtaDesktop =
      templateData?.template?.object_type === "Cta" && deviceType === "desktop";
    const isMobile = deviceType === "mobile";

    const updateIframeDimensions = () => {
      const height = container.clientHeight;
      const width = container.clientWidth;
      if (height <= 0 || width <= 0) return;

      if (isCtaDesktop) {
        const scaleX = width / 1332;
        const scale = Math.max(0.5, Math.min(scaleX * 0.98, 1.5));
        const dynamicHeight = Math.max(672, Math.round(height / scale));
        iframe.style.width = "1332px";
        iframe.style.height = `${dynamicHeight}px`;
        iframe.style.transform = `scale(${scale})`;
        iframe.style.transformOrigin = "top left";
      } else if (isMobile) {
        const scaleX = width / 375;
        const scaleY = height / 667;
        const scale = Math.min(scaleX, scaleY) * 0.98;
        const mobileScaleVal = Math.max(0.5, Math.min(scale, 1));
        iframe.style.width = "375px";
        iframe.style.height = "667px";
        iframe.style.transform = `scale(${mobileScaleVal})`;
        iframe.style.transformOrigin = "top left";
      } else {
        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
        iframe.style.transform = "";
      }
    };

    const runAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateIframeDimensions);
      });
    };

    updateIframeDimensions();
    runAfterLayout();
    const observer = new ResizeObserver(runAfterLayout);
    observer.observe(container);
    const timeoutIds = [100, 300, 600].map((ms) =>
      setTimeout(updateIframeDimensions, ms)
    );
    return () => {
      observer.disconnect();
      timeoutIds.forEach(clearTimeout);
    };
  }, [templateData, deviceType, iframeSrc]);

  useEffect(() => {
    if (modalRef.current) {
      $(modalRef.current).addClass("in").css("display", "block");
      $("body").addClass("modal-open");
    }
    return () => {
      if (modalRef.current) {
        $(modalRef.current).removeClass("in").css("display", "none");
      }
      $("body").removeClass("modal-open");
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!templateData) return null;

  const { template, user, account } = templateData;
  const creatorName =
    (user?.name || "a template creator") + " " + (user?.lastname || "");
  const avatarUrl =
    user?.avatar ||
    "https://s3.amazonaws.com/convertflow/uploads/4b2e7718-3df3-4f35-8a4f-28657bf36e0a/convertflow-icon-blue.png";

  const isCtaTemplate = template?.object_type === "Cta";
  const ctaDesktopIframeHeight =
    containerDimensions.height > 0 && fitScale > 0
      ? Math.max(672, Math.round(containerDimensions.height / fitScale))
      : 672;

  return (
    <div
      ref={modalRef}
      className="cf-template-preview-modal remix-editor modal fade"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="templatePreviewModalLabel"
      aria-modal="true"
      data-backdrop="false"
      style={{
        background: "rgba(0, 0, 0, 0.5)",
        overflowY: "auto",
        zIndex: 10001,
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-dialog !w-[100%] !max-w-[1600px] !h-[100%] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content hard-center bg-white dark:bg-slate-950 !shadow-2xl max-h-[95vh] flex flex-column overflow-x-hidden">
          <div className="modal-header !border-slate-200 dark:!border-slate-800">
            <button
              type="button"
              className="close dark:text-white"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div
                className="w-[45px] h-[45px] rounded-full bg-cover bg-center shrink-0"
                style={{ backgroundImage: `url(${avatarUrl})` }}
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-black dark:text-white text-[18px] font-semibold m-0 truncate">
                  {template.name}
                </h4>
                <p className="text-slate-500 text-xl m-0">
                  by {creatorName} at {account?.name || ""}
                </p>
              </div>
            </div>
          </div>

          <div className="modal-body flex flex-1 min-h-0 overflow-x-hidden relative">
            {contextData.remix_loading?.loading && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-slate-950"
                aria-hidden="false"
              >
                <RemixModalLoader
                  statusMessage={
                    contextData.remix_loading.isAiRemixing
                      ? "AI remixing content..."
                      : "Creating campaign..."
                  }
                  progress={contextData.remix_loading.progress}
                  showAiGeneratingStep={contextData.remix_loading.isAiRemixing}
                />
              </div>
            )}
            <div className="flex gap-5 my-3 px-7 flex-1 min-w-0 overflow-x-hidden">
              <div className="flex-1 flex flex-column min-w-0 max-w-[1205px]">
                <div
                  className="flex-1 flex flex-column items-start overflow-hidden bg-slate-100 rounded-xl min-h-0"
                  style={{
                    minHeight: 400,
                    paddingTop: 20,
                    paddingBottom: 24,
                    paddingLeft: 40,
                    paddingRight: 40,
                  }}
                >
                  <div
                    ref={iframeContainerRef}
                    className="relative flex-1 w-full min-h-0 flex justify-center items-start overflow-hidden"
                  >
                    {iframeSrc && (
                      <>
                        {isCtaTemplate && deviceType === "desktop" ? (
                          <div
                            ref={iframeWrapperRef}
                            className="absolute inset-0 flex justify-center items-start overflow-hidden"
                          >
                            <div
                              style={{
                                width: 1332 * fitScale,
                                height: "100%",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <iframe
                                ref={iframeRef}
                                id="template-preview-iframe"
                                src={iframeSrc}
                                title="Template preview"
                                frameBorder="0"
                                width={1332}
                                height={ctaDesktopIframeHeight}
                                style={{ transformOrigin: "top left" }}
                              />
                            </div>
                          </div>
                        ) : deviceType === "mobile" ? (
                          <div
                            ref={iframeWrapperRef}
                            className="absolute inset-0 flex justify-center items-center overflow-hidden"
                          >
                            <div
                              className="rounded-[2rem] overflow-hidden border-4 border-slate-700 bg-slate-800 shadow-xl"
                              style={{
                                width: 375 * mobileScale,
                                height: 667 * mobileScale,
                                flexShrink: 0,
                              }}
                            >
                              <iframe
                                ref={iframeRef}
                                id="template-preview-iframe"
                                src={iframeSrc}
                                title="Template preview"
                                frameBorder="0"
                                width={375}
                                height={667}
                                style={{
                                  transform: `scale(${mobileScale})`,
                                  transformOrigin: "top left",
                                  width: 375,
                                  height: 667,
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <iframe
                            ref={iframeRef}
                            id="template-preview-iframe"
                            src={iframeSrc}
                            title="Template preview"
                            frameBorder="0"
                            className="w-full h-full"
                          />
                        )}
                      </>
                    )}
                    {!contextData.remix_loading?.loading && (
                    <div
                      className="absolute left-3 flex rounded-[3px] overflow-hidden border-solid border-[1px] border-[#e6eff7] bg-white/80 shadow-sm z-10"
                      style={{ width: 100, height: 40, bottom: 22 }}
                    >
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center h-full cursor-pointer border-r border-solid border-[1px] border-[#e6eff7] ${
                          deviceType === "desktop"
                            ? "bg-[#e6eff7] text-slate-700"
                            : "bg-transparent text-slate-600 hover:bg-slate-50"
                        }`}
                        onClick={() => setDeviceType("desktop")}
                        title="Desktop"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="2"
                            y="3"
                            width="20"
                            height="14"
                            rx="2"
                            ry="2"
                          />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`flex-1 flex items-center justify-center h-full cursor-pointer border-solid border-[1px] border-transparent ${
                          deviceType === "mobile"
                            ? "bg-[#e6eff7] text-slate-700"
                            : "bg-transparent text-slate-600 hover:bg-slate-50"
                        }`}
                        onClick={() => setDeviceType("mobile")}
                        title="Mobile"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="5"
                            y="2"
                            width="14"
                            height="20"
                            rx="2"
                            ry="2"
                          />
                          <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-[400px] shrink-0 flex flex-column justify-between min-w-0">
                <RemixBuilderSettingsBreadcrumb options={STEP_OPTIONS} appendLabel="Generate" />

                {contextData.current_step === 1 && (
                  <RemixBuilderSettingsTheme applyThemeOnlyOnClick showBackButton={false} />
                )}
                {contextData.current_step === 2 && (
                  <TemplatePreviewModalSettingsDetails
                    templateData={templateData}
                    createEndpoint={createEndpoint}
                    redirectPath={redirectPath}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
